import type { Option } from '@branchjs/neo-components';

import type { Redirect as RedirectType } from '../../screens/ScreenLinkHubAdLinkCreator/steps/RedirectsStep/Redirects.types';
import { Redirect as RedirectEnum } from '../../screens/ScreenLinkHubAdLinkCreator/steps/RedirectsStep/Redirects.enums';
import type { AdLinkCreatorReducerType } from '../../reducers/AdLinkCreatorReducer/types/AdLinkCreatorReducerType';
import type { IAdLinkLinkData } from '../../../../../shared/types/links/Interfaces/IAdLinkLinkData';
import type { IAdLink, AdLinkFormat, IAdLinkAnalyticsTags, IBaseRedirects } from '../../../../../shared/types';
import { RedirectsClientOverride } from '../../../../../shared/types';

export class AdLinkCreatorService {
  private static getTitle = (adLink: AdLinkCreatorReducerType): string => {
    return adLink.title || '';
  };

  private static getAlias = (adLink: AdLinkCreatorReducerType): string | undefined => {
    return adLink.alias;
  };

  private static getPartner = (adLink: AdLinkCreatorReducerType): string => {
    return adLink.partner?.value || '';
  };

  private static getFormat = (adLink: AdLinkCreatorReducerType): AdLinkFormat => {
    if (adLink.kind === 'display') {
      return 'Cross-Platform Display';
    } else if (adLink.kind === 'search') {
      return 'Cross-Platform Search';
    } else {
      return 'App Only';
    }
  };

  private static getOsType = (adLink: AdLinkCreatorReducerType): string | undefined => {
    if (adLink.osPlatform === 'ios') {
      return 'iOS';
    } else if (adLink.osPlatform === 'android') {
      return 'Android';
    } else {
      return undefined;
    }
  };

  private static getTags = (tags: Option[]): string[] => {
    const parsedTags: string[] = [];

    if (tags) {
      tags.forEach((tag: Option) => {
        parsedTags.push(tag.value);
      });
    }

    return parsedTags;
  };

  private static getAnalyticsTags = (adLink: AdLinkCreatorReducerType): IAdLinkAnalyticsTags => {
    const analyticsTags = new Object() as IAdLinkAnalyticsTags;

    if (adLink.channel) {
      analyticsTags.channel = adLink.channel.value;
    }

    if (adLink.campaign) {
      analyticsTags.campaign = adLink.campaign.value;
    }

    const tags = this.getTags(adLink.tags as Option[]);
    if (tags.length > 0) {
      analyticsTags.tags = tags;
    }

    return analyticsTags;
  };

  private static getRedirectValidType = (redirect: RedirectType): RedirectsClientOverride => {
    if (redirect === RedirectEnum.Deepview) {
      return RedirectsClientOverride.Deepview;
    } else if (redirect === RedirectEnum.Web) {
      return RedirectsClientOverride.WebUrl;
    } else {
      return RedirectsClientOverride.DefaultRedirect;
    }
  };

  private static getRedirectValue = (redirect: string | Option): string => {
    if (typeof redirect === 'object') {
      return redirect.value;
    } else {
      return redirect;
    }
  };

  private static getRedirects = (adLink: AdLinkCreatorReducerType): IBaseRedirects => {
    const redirects: IBaseRedirects = new Object();

    if (adLink.ios) {
      redirects.ios = {
        override: this.getRedirectValidType(adLink.iosRedirect),
        value: adLink.ios ? this.getRedirectValue(adLink.ios) : '',
      };
    }

    if (adLink.android) {
      redirects.android = {
        override: this.getRedirectValidType(adLink.androidRedirect),
        value: adLink.android ? this.getRedirectValue(adLink.android) : '',
      };
    }

    if (adLink.desktop) {
      redirects.desktop = {
        override: this.getRedirectValidType(adLink.desktopRedirect),
        value: adLink.desktop ? this.getRedirectValue(adLink.desktop) : '',
      };
    }

    return redirects;
  };

  private static getLinkData = (adLink: AdLinkCreatorReducerType): IAdLinkLinkData & Record<string, string> => {
    const linkData = new Object() as IAdLinkLinkData & Record<string, string>;

    adLink?.linkData?.forEach((entry) => {
      if (entry.key && entry.value) {
        linkData[entry.key.value] = typeof entry.value === 'string' ? entry.value.trim() : entry.value;
      }
    });

    return linkData;
  };

  private static getDomain = (adLink: AdLinkCreatorReducerType): string | undefined => {
    return adLink?.domain;
  };

  public static getAdLinkPayload = (adLink: AdLinkCreatorReducerType): IAdLink => {
    return {
      title: this.getTitle(adLink),
      alias: this.getAlias(adLink),
      partner: this.getPartner(adLink),
      format: this.getFormat(adLink),
      osType: this.getOsType(adLink),
      analyticsTags: this.getAnalyticsTags(adLink),
      redirects: this.getRedirects(adLink),
      linkData: this.getLinkData(adLink),
      domain: this.getDomain(adLink),
    };
  };
}
